import * as React from "react"
import fetch from "isomorphic-fetch"
import Client from "shopify-buy"
import { addToCart, updateCart, createCart, getCartById } from '../utils/cartService'

const client = Client.buildClient(
  {
    domain: process.env.GATSBY_MYSHOPIFY_URL,
    storefrontAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN
  },
  fetch
)

const defaultValues = {
  cart: [],
  isOpen: false,
  loading: false,
  onOpen: () => {},
  onClose: () => {},
  addVariantToCart: () => {},
  removeLineItem: () => {},
  updateLineItem: () => {},
  client,
  checkout: {
    lineItems: [],
  },
}

export const StoreContext = React.createContext(defaultValues)

const isBrowser = typeof window !== `undefined`
const localStorageKey = `shopify_cart_id`

// Helper function to transform cart data to match the expected format
const transformCartData = (cartData) => {
  if (!cartData) {
    return {
      id: 'cart',
      webUrl: null,
      lineItems: []
    }
  }

  return {
    id: cartData.id,
    webUrl: cartData.checkoutUrl,
    lineItems: cartData.lines?.edges?.map(edge => ({
      id: edge.node.id,
      quantity: edge.node.quantity,
      title: edge.node.merchandise.product.title,
      variant: {
        id: edge.node.merchandise.id,
        title: edge.node.merchandise.title,
        priceV2: edge.node.merchandise.priceV2,
        image: edge.node.merchandise.product.images?.edges?.[0]?.node ? {
          src: edge.node.merchandise.product.images.edges[0].node.url,
          originalSrc: edge.node.merchandise.product.images.edges[0].node.url,
          altText: edge.node.merchandise.product.images.edges[0].node.altText
        } : null
      }
    })) || []
  }
}

export const StoreProvider = ({ children }) => {
  const [checkout, setCheckout] = React.useState(defaultValues.checkout)
  const [loading, setLoading] = React.useState(false)
  const [didJustAddToCart, setDidJustAddToCart] = React.useState(false)

  const setCheckoutItem = (checkout) => {
    if (isBrowser) {
      localStorage.setItem(localStorageKey, checkout.id)
    }

    setCheckout(checkout)
  }

  React.useEffect(() => {
    const initializeCart = async () => {
      try {
        // Clear any old localStorage data from shopify-buy
        if (isBrowser) {
          localStorage.removeItem('shopify_checkout_id')
        }

        // Check if we have a stored cart ID
        const existingCartID = isBrowser ? localStorage.getItem(localStorageKey) : null

        if (existingCartID && existingCartID !== `null` && existingCartID.startsWith('gid://')) {
          try {
            // Try to fetch existing cart (only if it's a valid Storefront API cart ID)
            const cartData = await getCartById(existingCartID)
            if (cartData) {
              const transformedCart = transformCartData(cartData)
              setCheckoutItem(transformedCart)
              return
            }
          } catch (e) {
            console.error('Error fetching existing cart:', e)
            localStorage.setItem(localStorageKey, null)
          }
        }

        // Create new cart
        const cartData = await createCart()
        const transformedCart = transformCartData(cartData)
        setCheckoutItem(transformedCart)
      } catch (e) {
        console.error('Error initializing cart:', e)
        // Create empty cart structure on error
        const emptyCart = transformCartData(null)
        setCheckoutItem(emptyCart)
      }
    }

    initializeCart()
  }, [])

  const addVariantToCart = async (variantId, quantity) => {
    setLoading(true)

    try {
      // Use Storefront API to add item
      const cartData = await addToCart(checkout.id, variantId, quantity)
      
      // Transform cart data to match the expected format
      const transformedCart = transformCartData(cartData)
      setCheckout(transformedCart)
      setDidJustAddToCart(true)
      setTimeout(() => setDidJustAddToCart(false), 3000)
    } catch (error) {
      console.error('Error adding to cart:', error)
    } finally {
      setLoading(false)
    }
  }

  const removeLineItem = async (checkoutID, lineItemId) => {
    setLoading(true)

    try {
      // Use Storefront API to remove item (set quantity to 0)
      const cartData = await updateCart(checkoutID, lineItemId, 0)
      
      // Transform cart data to match the expected format
      const transformedCart = transformCartData(cartData)
      setCheckout(transformedCart)
    } catch (error) {
      console.error('Error removing from cart:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateLineItem = async (checkoutID, lineItemId, quantity) => {
    setLoading(true)

    try {
      // Use Storefront API to update item
      const cartData = await updateCart(checkoutID, lineItemId, quantity)
      
      // Transform cart data to match the expected format
      const transformedCart = transformCartData(cartData)
      setCheckout(transformedCart)
    } catch (error) {
      console.error('Error updating cart:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        addVariantToCart,
        removeLineItem,
        updateLineItem,
        checkout,
        loading,
        didJustAddToCart,
      }}
    >
      {children}
    </StoreContext.Provider>
  )
}